import type { FC } from "react";
import { useMemo } from "react";

import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { checkUrlEquivalence } from "@jti/fe-common";
import { ICartItemErrorType, type Product } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { I18n, useTranslations } from "@jti/ui";

import { useOrderLimit } from "@checkout/hooks/useOrderLimit";
import { filterItemsByErrorCode } from "@checkout/utils/getCartErrors";
import { getOutOfStockItems } from "@checkout/utils/getOutOfStockItems";

import { CheckoutErrorMessage } from "./CheckoutErrorMessage/CheckoutErrorMessage";
import { OutOfStockItems } from "./OutOfStockItems/OutOfStockItems";

import { PriceLimitErrorMessage } from "./PriceLimitErrorMessage";

import styles from "./SingleError.module.scss";

type CheckoutErrorSummaryProps = {
  items: Product[];
  onRemoveItems(itemIds: string[]): Promise<void>;
  className?: string;
  variant?: "regular" | "compact";
};

export const CheckoutErrorSummary: FC<CheckoutErrorSummaryProps> = ({
  onRemoveItems,
  items,
  className,
  variant = "regular",
}) => {
  const { __: translate } = useTranslations();
  const { PROFILE_PAGE_URL, CART_CHECKOUT_PAGE_URL } = useConfig();
  const mySubscriptionPage = PROFILE_PAGE_URL + "#/subscription";
  const outOfStockItems = getOutOfStockItems(items);
  const { isOrderLimitError, maxOrderLimit } = useOrderLimit();
  const cartonsLimitValue = useSelector((state) => state.cart.storeConfig?.cartons_limit_value);
  const packsLimitValue = cartonsLimitValue * 10;

  const ongoingSubscriptionItems = useMemo(
    () => filterItemsByErrorCode(items, ICartItemErrorType.OngoingSubscription),
    [items]
  );

  const subscriptionLimitReachedItems = useMemo(
    () => filterItemsByErrorCode(items, ICartItemErrorType.CartonsLimit),
    [items]
  );

  const restrictedSinglePurchaseItems = useMemo(
    () => filterItemsByErrorCode(items, ICartItemErrorType.LifetimePurchaseRestricted),
    [items]
  );

  const trueConditionsCount = [
    outOfStockItems.length > 0,
    ongoingSubscriptionItems.length > 0,
    subscriptionLimitReachedItems.length > 0,
    restrictedSinglePurchaseItems.length > 0,
    isOrderLimitError,
  ].filter(Boolean).length;

  const shouldDisplayErrorBox = trueConditionsCount >= 1;

  const isBoxWithMultipleErrors = trueConditionsCount >= 2;

  if (!shouldDisplayErrorBox) {
    return null;
  }

  const onRemoveWithRedirect = async (itemsIds: string[], redirectUrl: string) => {
    await onRemoveItems(itemsIds);

    if (checkUrlEquivalence(window.location.href, redirectUrl)) {
      window.location.reload();
    } else {
      window.location.href = redirectUrl;
    }
  };

  return (
    <div className={classNames(styles.marginBottom, className)}>
      <AlertBanner
        tone="warning"
        richDescription={
          <>
            {isBoxWithMultipleErrors && variant === "regular" && (
              <p className={classNames(styles.title, styles.titleRegular)}>{translate("cart.items.multipleError")}</p>
            )}
            {outOfStockItems.length > 0 && (
              <div className={styles.singleErrorBox}>
                <OutOfStockItems items={outOfStockItems} className={styles.outOfStock} variant={variant} />
              </div>
            )}
            {isOrderLimitError && (
              <div className={styles.singleErrorBox}>
                <PriceLimitErrorMessage variant={variant} maxOrderLimit={maxOrderLimit} />
              </div>
            )}
            {subscriptionLimitReachedItems.length > 0 && (
              <div className={styles.singleErrorBox}>
                <CheckoutErrorMessage
                  items={subscriptionLimitReachedItems}
                  variant={variant}
                  title={translate("cart.items.subscription_limit_reached_message_box.title")}
                  intro={
                    <I18n t="cart.items.subscription_limit_reached_message_box.intro">
                      <b>
                        <I18n t="cart.items.subscription_limit_reached_message_box.introMiddle">
                          <>{cartonsLimitValue}</>
                          <>{packsLimitValue}</>
                        </I18n>
                      </b>
                    </I18n>
                  }
                />
              </div>
            )}
            {restrictedSinglePurchaseItems.length > 0 && (
              <div className={styles.singleErrorBox}>
                <CheckoutErrorMessage
                  items={restrictedSinglePurchaseItems}
                  onRemoveItems={(itemsIds) => onRemoveWithRedirect(itemsIds, CART_CHECKOUT_PAGE_URL)}
                  variant={variant}
                  title={translate("cart.items.restricted_purchase_message_box.title")}
                  intro={
                    <div className={styles.introItemsListWrapper}>
                      <p className={styles.intro}>{translate("cart.items.restricted_purchase_message_box.intro")}</p>
                      {!!restrictedSinglePurchaseItems.length && (
                        <ul className={styles.introItemsList}>
                          {restrictedSinglePurchaseItems.map((item) => (
                            <li key={item.name} className={styles.introItemsListItem}>
                              {item.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  }
                  buttonText={translate("cart.items.restricted_purchase_message_box.remove_button")}
                />
              </div>
            )}
            {ongoingSubscriptionItems.length > 0 && (
              <div className={styles.singleErrorBox}>
                <CheckoutErrorMessage
                  items={ongoingSubscriptionItems}
                  onRemoveItems={(itemsIds) => onRemoveWithRedirect(itemsIds, mySubscriptionPage)}
                  variant={variant}
                  title={translate("cart.items.ongoing_subscriptions_message_box.title")}
                  intro={translate("cart.items.ongoing_subscriptions_message_box.intro")}
                  buttonText={translate("cart.items.ongoing_subscriptions_message_box.remove_button")}
                />
              </div>
            )}
          </>
        }
      />
    </div>
  );
};
